/**
 * Calendar-day arithmetic in the DEVICE'S LOCAL timezone.
 *
 * Built on the `Date(year, month, day)` constructor rather than on `+ 24h`: a local day is not
 * always 24 hours long. A spring-forward day is 23 hours and a fall-back day is 25, and "tomorrow"
 * means the next calendar date on the wall clock, not a fixed number of milliseconds from now. The
 * constructor resolves the wall-clock instant for us, including the rare zones whose day starts at
 * a time other than 00:00 on a transition day.
 */

/** Midnight at the start of `date`'s calendar day, in local time. */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Midnight at the start of the NEXT local calendar day — the instant "tomorrow" begins.
 *
 * This is what "skip until tomorrow" persists: the absolute instant, computed once at the moment
 * of the skip, so a restarted app and a synced device release the card at the same time whatever
 * their clocks did in between.
 */
export function startOfNextLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

/**
 * `YYYY-MM-DD` for `date`'s local calendar day.
 *
 * Stable for the whole day and changes exactly at local midnight, which makes it a good query-key
 * ingredient for reads whose answer depends on "what day is it" (a deferral expiring, a daily
 * count): the key rolls over when the day does, and not before.
 */
export function localDayKey(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
